const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const DAY_TO_NUM = Object.fromEntries(
  DAY_NAMES.map((n, i) => [n.slice(0, 3).toUpperCase(), String(i)])
);

const isStar = (s: string) => s === "*";
const isNum = (s: string) => /^\d+$/.test(s);
const isStep = (s: string) => /^\*\/\d+$/.test(s);
const isRange = (s: string) => /^\d+-\d+$/.test(s);
const isList = (s: string) => /^\d+(,\d+)+$/.test(s);

const stepVal = (s: string) => Number(s.slice(s.indexOf("/") + 1));
const rangeVals = (s: string) => s.split("-").map(Number) as [number, number];
const listVals = (s: string) => s.split(",").map(Number);

const formatHour = (h: number) =>
  h === 0
    ? "midnight"
    : h === 12
      ? "noon"
      : h > 12
        ? `${h - 12} PM`
        : `${h} AM`;

const formatTime = (m: number, h: number) =>
  m === 0
    ? formatHour(h)
    : `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function joinNatural(items: string[]): string {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

const normalizeDow = (s: string) =>
  s
    .toUpperCase()
    .replace(/SUN|MON|TUE|WED|THU|FRI|SAT/g, (m) => DAY_TO_NUM[m] ?? m);

function describeDayOfWeek(dow: string): string | null {
  if (isStar(dow)) return null;
  if (dow === "1-5") return "weekdays";
  if (isNum(dow)) return `${DAY_NAMES[Number(dow) % 7]}s`;
  if (isRange(dow)) {
    const [a, b] = rangeVals(dow);
    return `${DAY_NAMES[a % 7]} to ${DAY_NAMES[b % 7]}`;
  }
  if (isList(dow)) {
    const nums = [...new Set(listVals(dow).map((n) => n % 7))].sort(
      (a, b) => a - b
    );
    if (nums.length === 2 && nums[0] === 0 && nums[1] === 6) return "weekends";
    return joinNatural(nums.map((d) => DAY_NAMES[d] as string));
  }
  return null;
}

function describeDayOfMonth(dom: string): string | null {
  if (isStar(dom) || !isNum(dom)) return null;
  const n = Number(dom);
  const v = n % 100;
  const tail =
    v >= 11 && v <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `on the ${n}${tail}`;
}

function describeHourSpec(hour: string): string | null {
  if (isStar(hour)) return null;
  if (isNum(hour)) return `during the ${formatHour(Number(hour))} hour`;
  if (isRange(hour)) {
    const [a, b] = rangeVals(hour);
    return `${formatHour(a)} to ${formatHour(b)}`;
  }
  if (isList(hour)) {
    const hs = listVals(hour).sort((a, b) => a - b);
    if (hs.every((h) => h >= 0 && h <= 23))
      return `during the ${joinNatural(hs.map(formatHour))} hours`;
  }
  return null;
}

function describeSpecificTimes(minute: string, hour: string): string | null {
  const mOk = isNum(minute) || isList(minute);
  const hOk = isNum(hour) || isList(hour);
  if (!mOk || !hOk || (isList(minute) && isList(hour))) return null;
  const ms = listVals(minute);
  const hs = listVals(hour);
  if (
    !ms.every((m) => m >= 0 && m <= 59) ||
    !hs.every((h) => h >= 0 && h <= 23)
  )
    return null;
  const times: { k: number; s: string }[] = [];
  for (const h of hs)
    for (const m of ms) times.push({ k: h * 60 + m, s: formatTime(m, h) });
  times.sort((a, b) => a.k - b.k);
  return joinNatural(times.map((t) => t.s));
}

function describeMinuteFreq(minute: string): string | null {
  if (isStar(minute)) return "Every minute";
  if (!isStep(minute)) return null;
  const n = stepVal(minute);
  return n === 1 ? "Every minute" : `Every ${n} minutes`;
}

function describeHourlyFreq(minute: string, hour: string): string | null {
  if (isNum(minute)) {
    const at = Number(minute) === 0 ? "" : ` at :${minute.padStart(2, "0")}`;
    if (isStar(hour) || isRange(hour)) return `Every hour${at}`;
    if (isStep(hour)) {
      const n = stepVal(hour);
      return n === 1 ? `Every hour${at}` : `Every ${n} hours${at}`;
    }
    return null;
  }
  if (isList(minute) && (isStar(hour) || isRange(hour))) {
    const ms = listVals(minute).sort((a, b) => a - b);
    if (!ms.every((m) => m >= 0 && m <= 59)) return null;
    const items = ms.map((m) => `:${String(m).padStart(2, "0")}`);
    return `Every hour at ${joinNatural(items)}`;
  }
  return null;
}

export function describeCron(expr: string): string {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [minute, hour, dom, month, dowRaw] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];
  const dow = normalizeDow(dowRaw);

  if (!isStar(month)) return expr;
  if (!isStar(dom) && !isStar(dow)) return expr;

  const dowDesc = describeDayOfWeek(dow);
  const domDesc = describeDayOfMonth(dom);
  if (!isStar(dom) && !domDesc) return expr;
  if (!isStar(dow) && !dowDesc) return expr;

  const times = describeSpecificTimes(minute, hour);
  if (times) {
    if (isStar(dom) && isStar(dow)) return `Daily at ${times}`;
    if (dowDesc) return `${capitalize(dowDesc)} at ${times}`;
    if (domDesc) return `${capitalize(domDesc)} at ${times}`;
  }

  const minuteFreq = describeMinuteFreq(minute);
  const hourSpec = describeHourSpec(hour);
  let phrase: string;

  if (minuteFreq) {
    if (!isStar(hour) && !hourSpec) return expr;
    phrase = minuteFreq;
  } else {
    const hourly = describeHourlyFreq(minute, hour);
    if (!hourly) return expr;
    if (!isStep(hour) && !isStar(hour) && !hourSpec) return expr;
    phrase = hourly;
  }

  const fragments = [phrase];
  if (!isStep(hour) && hourSpec) fragments.push(hourSpec);
  if (domDesc) fragments.push(domDesc);
  if (dowDesc) fragments.push(dowDesc);
  return fragments.join(", ");
}
